import React, { useState, useEffect, useRef } from 'react';
import { marked } from 'marked';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  AssistantChatRequest,
  AssistantChatResponse,
  AssistantConversation,
  AssistantMessage,
  AssistantAction,
  KnowledgeSearchResult,
  ClarifyingQuestion,
  PendingActionPreview,
  McpProposalPreview,
  McpProvisionRequest,
  McpConfigureBuiltinPreview,
  McpConfigureBuiltinRequest,
  ScheduleConfirmPreview,
  ScheduleConfirmRequest,
  Journey
} from '../../types';
import apiService from '../../services/api';
import KnowledgeBasePanel from './KnowledgeBasePanel';
import ClarifyChips from './ClarifyChips';
import PreviewCard from './PreviewCard';
import CompactRunProgressCard from './CompactRunProgressCard';
import McpProposalCard from './McpProposalCard';
import McpConfigureBuiltinCard from './McpConfigureBuiltinCard';
import ScheduleConfirmCard from './ScheduleConfirmCard';
import JourneyProgress from './JourneyProgress';
import Logo from '../common/Logo';
import { useProjectStore } from '../../store';
import ModelPickerField from '../common/ModelPickerField';
import {
  useAssistantModels,
  useAssistantModelPreference,
  useSetAssistantModelPreference,
} from '../../hooks/queries/useAssistantModels';
import {
  useConfirmMcpProposal,
  useDeclineMcpProposal,
  useConfirmBuiltinSetup,
  useDeclineBuiltinSetup,
} from '../../hooks/queries/useMcpDiscovery';
import {
  useConfirmScheduleSetup,
  useDeclineScheduleSetup,
} from '../../hooks/queries/useScheduleSetup';
import { queryKeys } from '../../hooks/queries/queryKeys';

interface AssistantChatProps {
  projectId?: number;
  contextType?: 'project' | 'orchestration' | 'agent' | 'general';
  contextId?: number;
  initialConversationId?: number;
  onClose?: () => void;
}

/**
 * A resource the assistant just created (project / agent / workflow), distilled
 * from a structured, executed AssistantAction.result. The id/projectId come from
 * the backend CRUD row — never from model-authored text — so the navigation link
 * is always correct. Rendered as a persistent "Open <name>" control in the reply.
 */
type CreatedResource = {
  type: 'project' | 'agent' | 'workflow' | 'integration';
  id: number; // the resource's own id (used for the React key)
  // Owning project (for store sync + route building). 0 for user-scoped
  // integrations, which live under Settings rather than a project.
  projectId: number;
  name: string;
  navigateTo: string;
};

/**
 * Lifecycle state of a per-message plan preview (AC-1646). Bound to the message
 * that carried the preview, never to shared component state — a later turn
 * without a preview must not re-show or mutate an earlier turn's card.
 *
 * AC-1738: the union is widened with the MCP-proposal lifecycle words
 * ('provisioning' | 'provisioned') so the same per-message slot can drive either
 * the create/update PreviewCard or the McpProposalCard. A given message carries
 * exactly one of the two preview shapes, never both.
 */
type PreviewState =
  | 'pending'
  | 'creating'
  | 'created'
  | 'declined'
  | 'superseded'
  | 'provisioning'
  | 'provisioned'
  // AC-1746: the in-chat built-in configure lifecycle words, so the same
  // per-message slot can also drive the McpConfigureBuiltinCard.
  | 'configuring'
  | 'configured'
  // AC-1790: the in-chat schedule-setup lifecycle words, so the same per-message
  // slot can also drive the ScheduleConfirmCard.
  | 'confirming'
  | 'confirmed';

/** A pending preview that may be a create/update plan, a Tier-3 MCP proposal, or
 *  (AC-1746) an unconfigured-built-in in-chat configure card. A message carries
 *  exactly one of these shapes, never more than one. */
type AnyPendingPreview =
  | PendingActionPreview
  | McpProposalPreview
  | McpConfigureBuiltinPreview
  | ScheduleConfirmPreview;

/**
 * AC-1738: narrow a pending preview to the Tier-3 MCP-proposal variant. The
 * backend tags it with `action_type === 'propose_mcp_server'` and carries a
 * `candidate` + `shortlist` instead of the create/update plan fields.
 */
const isMcpProposalPreview = (
  preview: AnyPendingPreview & { state: PreviewState },
): preview is McpProposalPreview & { state: PreviewState } =>
  preview.action_type === 'propose_mcp_server';

/**
 * AC-1746 (defect AC-1743): narrow a pending preview to the unconfigured-built-in
 * in-chat configure variant. The backend tags it with
 * `action_type === 'configure_builtin_mcp'` and carries `builtin_server_id` +
 * `required_credentials` so the card can render the built-in setup form.
 */
const isConfigureBuiltinPreview = (
  preview: AnyPendingPreview & { state: PreviewState },
): preview is McpConfigureBuiltinPreview & { state: PreviewState } =>
  preview.action_type === 'configure_builtin_mcp';

/**
 * AC-1790 (epic AC-1787): narrow a pending preview to the concierge
 * schedule-setup variant. The backend tags it with
 * `action_type === 'configure_schedule'` and carries `orchestration_id` +
 * `suggested_schedule` so the card can render the prefilled cadence controls.
 */
const isScheduleConfirmPreview = (
  preview: AnyPendingPreview & { state: PreviewState },
): preview is ScheduleConfirmPreview & { state: PreviewState } =>
  preview.action_type === 'configure_schedule';

interface Message {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  actions?: AssistantAction[];
  suggestedActions?: string[];
  createdResources?: CreatedResource[];
  // AC-1645: clarifying questions rendered as one-click answer chips on this turn.
  clarifyingQuestions?: ClarifyingQuestion[];
  // AC-1646: a plan preview gating creation, with its own per-message lifecycle.
  // AC-1738: may instead carry an MCP-proposal preview (propose_mcp_server),
  // rendered as an McpProposalCard. AC-1746: or a configure_builtin_mcp preview,
  // rendered as an McpConfigureBuiltinCard. A message carries one shape, never
  // more than one.
  pendingActionPreview?: AnyPendingPreview & {
    state: PreviewState;
  };
  // AC-1646: banner shown when a confirm came back as a 409 superseded plan.
  planChanged?: boolean;
  // AC-1785 (epic AC-1778): the live-run handle for a workflow the assistant
  // just ran on confirm. Carries the ids the CompactRunProgressCard needs to
  // subscribe to useExecution(orchestration_id) and build the Open-workflow deep
  // link. Present only on the follow-up turn appended after a run confirm.
  runProgress?: {
    orchestrationId: number;
    runInstanceId?: number;
    stepCount?: number;
    projectId?: number;
    workflowName?: string;
  };
  // AC-1757 (epic AC-1749 Assistant Concierge): the goal-journey envelope this
  // turn carried, read off response.journey (chat / confirm-builtin / build
  // confirm / decline). When present, a JourneyProgress strip renders above this
  // turn's pending-action card. Purely derived response metadata — never
  // persisted client-side.
  journey?: Journey;
}

/**
 * AC-1757: a journey is "open" — i.e. the concierge should auto-continue to the
 * next consequential card — only when it is present, NOT complete, and NOT
 * blocked (no blocked_reason and no step marked 'blocked'). A complete or blocked
 * journey ends the chain, so it never triggers an auto-continue.
 */
const isJourneyOpen = (journey?: Journey): boolean =>
  !!journey &&
  !journey.complete &&
  !journey.blocked_reason &&
  !journey.steps?.some((s) => s.status === 'blocked');

/**
 * Map executed, successful create_* actions to navigable CreatedResource entries.
 * Pure and side-effect free so it can be unit-tested in isolation. A resource is
 * omitted (rather than producing a broken route) when the fields needed to build
 * its link are missing — e.g. an orchestration result without a project_id.
 */
const resolveCreatedResources = (actions: AssistantAction[]): CreatedResource[] =>
  actions
    .filter((a) => a.status === 'executed' && a.result?.success)
    .map((a): CreatedResource | null => {
      const r = a.result as Record<string, any>;
      if (a.action_type === 'create_project' && r.project_id != null) {
        return {
          type: 'project',
          id: r.project_id,
          projectId: r.project_id,
          name: r.project_name || 'New Project',
          navigateTo: `/projects/${r.project_id}`,
        };
      }
      if (a.action_type === 'create_agent' && r.project_id != null && r.agent_id != null) {
        return {
          type: 'agent',
          id: r.agent_id,
          projectId: r.project_id,
          name: r.agent_name || 'New Agent',
          navigateTo: `/projects/${r.project_id}/agents`,
        };
      }
      if (
        a.action_type === 'create_simple_orchestration' &&
        r.project_id != null &&
        r.orchestration_id != null
      ) {
        return {
          type: 'workflow',
          id: r.orchestration_id,
          projectId: r.project_id,
          name: r.orchestration_name || 'New Workflow',
          navigateTo: `/projects/${r.project_id}/workflows/${r.orchestration_id}`,
        };
      }
      // AC-1787 (defect fix): the journey's FINAL step — a confirmed
      // configure_schedule — surfaces an "Open <workflow>" control on the
      // schedule/complete turn, mirroring create_simple_orchestration. The
      // backend confirm result now carries project_id + orchestration_id +
      // orchestration_name, so the deep link is built from the structured row
      // (never model-authored text). A result missing project_id/orchestration_id
      // is omitted rather than producing a broken route.
      if (
        a.action_type === 'configure_schedule' &&
        r.project_id != null &&
        r.orchestration_id != null
      ) {
        return {
          type: 'workflow',
          id: r.orchestration_id,
          projectId: r.project_id,
          name: r.orchestration_name || 'Workflow',
          navigateTo: `/projects/${r.project_id}/workflows/${r.orchestration_id}`,
        };
      }
      // AC-1740: an assistant-provisioned remote MCP server surfaces an "Open
      // Integrations" control that lands on the page where the new server now
      // appears. Project-scoped servers live on the project Integrations page;
      // user-scoped servers live under Settings. The id comes from the backend
      // provision result, never from model-authored text.
      if (a.action_type === 'provision_mcp_server' && r.mcp_server_id != null) {
        const isUserScope = r.scope === 'user' || r.project_id == null;
        return {
          type: 'integration',
          id: r.mcp_server_id,
          projectId: isUserScope ? 0 : r.project_id,
          name: r.server_name ? `${r.server_name} Integration` : 'Integrations',
          navigateTo: isUserScope
            ? '/settings'
            : `/projects/${r.project_id}/integrations`,
        };
      }
      return null;
    })
    .filter((r): r is CreatedResource => r !== null);

interface OperationContext {
  name: string;
  description: string;
  agent_count: number;
  orchestration_count: number;
  model_count: number;
  created_at: string;
  updated_at: string;
  recent_orchestrations: Array<{
    name: string;
    description: string;
    step_count: number;
  }>;
  project_id: number;
}

interface WorkflowContext {
  orchestration_id: number;
  name: string;
  description: string;
  project_id: number;
  project_name: string;
  created_at: string;
  updated_at: string;
  total_steps: number;
  step_types: Record<string, number>;
  agents_used_count: number;
  agents_used: number[];
  steps: Array<{
    step_number: number;
    step_id: number;
    type: string;
    label: string;
    description: string;
    agent_id?: number;
    agent_name?: string;
    has_conditions: boolean;
    execution_mode: string;
    fan_out: boolean;
    conditional_logic?: boolean;
    on_true_action?: any;
    on_false_action?: any;
  }>;
  execution_stats: {
    total_runs: number;
    successful_runs: number;
    failed_runs: number;
    pending_runs: number;
    running_runs: number;
    success_rate: number;
  };
  most_recent_run?: {
    run_id: number;
    status: string;
    created_at: string;
    current_step_id?: number;
  };
  summary: string;
  trace_enabled: boolean;
}

/**
 * AC: localStorage key under which the active assistant conversation id is
 * persisted, so the chat panel restores its history across an SPA navigation or
 * reload (e.g. when the user clicks "Open <workflow>" and the panel remounts in
 * the destination route). conversationId otherwise lives only in component state
 * and Layout never passes initialConversationId, so the panel would restart
 * empty. The stored id is read on mount and removed when the conversation clears.
 */
const ASSISTANT_CONVERSATION_ID_KEY = 'ac.assistantConversationId';

const AssistantChat: React.FC<AssistantChatProps> = ({
  projectId,
  contextType = 'general',
  contextId,
  initialConversationId,
  onClose
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { loadProjects, setCurrentOperation } = useProjectStore();

  // AC-1616: assistant model selection. The allow-list (mid-tier+, key-gated)
  // and the user's saved preference drive a compact picker in the header. The
  // set-preference mutation persists the choice and invalidates the preference
  // key so the picker re-reads it. Reads resolve from cache when seeded.
  const { data: assistantModels } = useAssistantModels();
  const { data: assistantPreference } = useAssistantModelPreference();
  const setAssistantModelPreference = useSetAssistantModelPreference();
  const selectedAssistantModel =
    assistantPreference?.model ?? assistantModels?.default ?? 'gpt-4o';

  // AC-1738: MCP-proposal approval-gate mutations. Confirm provisions the remote
  // server (carrying scope + credentials) and invalidates the MCP servers query
  // so it appears in integrations; decline writes nothing.
  const confirmMcpProposal = useConfirmMcpProposal();
  const declineMcpProposal = useDeclineMcpProposal();

  // AC-1746: in-chat built-in (Email) configure-gate mutations. Confirm saves the
  // built-in's credentials (encrypted, server-side) and invalidates the built-in
  // credential status + MCP servers queries so the integrations UI and any "is
  // this built-in configured?" read refresh; decline writes nothing.
  const confirmBuiltinSetup = useConfirmBuiltinSetup();
  const declineBuiltinSetup = useDeclineBuiltinSetup();

  // AC-1790 (epic AC-1787): in-chat schedule-setup gate mutations. Confirm creates
  // a recurring schedule for the just-built email-summary workflow (carrying the
  // final cadence the user adjusted) and invalidates that workflow's schedule
  // query so any ScheduleConfigPanel / ScheduleIndicator subscriber refreshes;
  // decline writes nothing.
  const confirmScheduleSetup = useConfirmScheduleSetup();
  const declineScheduleSetup = useDeclineScheduleSetup();

  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // AC-1767: true while a system-driven auto-continue turn round-trips. Drives
  // the centered, clearly-automatic "Continuing setup…" indicator (role=status)
  // that replaces the formerly-fabricated user "continue" bubble, so the journey
  // advance is transparent rather than misattributed to the user.
  const [isAutoContinuing, setIsAutoContinuing] = useState(false);
  // AC: the copy shown in the centered auto-continue indicator for THIS silent
  // turn. Defaults to "Continuing setup…" (the AC-1767 journey auto-continue);
  // a caller may override it for an honest, action-specific pill — e.g. the
  // "Run it now" button passes "Starting the run…" so the silent run turn reads
  // as a deliberate system action, not a fabricated "run the workflow" user
  // message. Cleared in the send path's finally block alongside isAutoContinuing.
  const [silentIndicatorText, setSilentIndicatorText] = useState<string | null>(null);
  // AC: conversationId is seeded from the initialConversationId prop OR a value
  // persisted to localStorage on a prior turn, so the assistant panel survives an
  // SPA navigation / reload (e.g. after the user clicks "Open <workflow>"). See
  // the persist-on-change and restore-on-mount effects below.
  const [conversationId, setConversationId] = useState<number | null>(
    initialConversationId ?? (Number(localStorage.getItem(ASSISTANT_CONVERSATION_ID_KEY)) || null)
  );
  const [isKnowledgePanelOpen, setIsKnowledgePanelOpen] = useState(false);
  const [contextUsed, setContextUsed] = useState<KnowledgeSearchResult[]>([]);
  const [projectContext, setOperationContext] = useState<OperationContext | null>(null);
  const [workflowContext, setWorkflowContext] = useState<WorkflowContext | null>(null);
  const [showOperationContext, setShowOperationContext] = useState(false);
  const [showWorkflowContext, setShowWorkflowContext] = useState(false);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [newOperationId, setNewOperationId] = useState<number | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // AC-1757: tracks the message ids whose journey already triggered a one-shot
  // auto-continue, so the concierge advances at most once per confirmed step
  // (no force-loop). A complete/blocked journey is never added here.
  const autoContinuedMessageIds = useRef<Set<number>>(new Set());

  // Auto-grow the message textarea up to a max height, then let it scroll.
  const MAX_INPUT_HEIGHT_PX = 160;
  const autoSizeInput = (el: HTMLTextAreaElement | null) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_INPUT_HEIGHT_PX)}px`;
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputMessage(e.target.value);
    autoSizeInput(e.target);
  };

  const scrollToBottom = () => {
    // Guard scrollIntoView: it is undefined in non-browser environments
    // (jsdom does not implement it), so calling it unguarded would throw.
    messagesEndRef.current?.scrollIntoView?.({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // AC: persist the active conversation id so the panel can restore its history
  // across an SPA navigation / reload. Layout never passes initialConversationId,
  // so without this the conversation is lost the moment the panel remounts (e.g.
  // after clicking "Open <workflow>"). Store the number while a conversation is
  // active; remove the key once it clears so a fresh session starts clean.
  useEffect(() => {
    if (conversationId != null) {
      localStorage.setItem(ASSISTANT_CONVERSATION_ID_KEY, String(conversationId));
    } else {
      localStorage.removeItem(ASSISTANT_CONVERSATION_ID_KEY);
    }
  }, [conversationId]);

  // AC: guards the one-shot restore so an in-flight or completed restore is never
  // re-run (the resolved conversationId is a dependency, but the load must fire at
  // most once per mounted panel).
  const didRestoreConversationRef = useRef(false);

  // Restore existing conversation messages on mount. Generalized (AC) to fire for
  // a conversationId resolved EITHER from the initialConversationId prop OR from
  // the localStorage-persisted id, so the panel survives navigation/reload — not
  // only the explicit-resume prop path. Guarded so it loads at most once and only
  // when the transcript is still empty (a freshly-sent turn must not be clobbered).
  useEffect(() => {
    const loadConversation = async () => {
      if (conversationId == null) return;
      if (didRestoreConversationRef.current) return;
      // Only restore into an empty transcript: if the user already has live
      // messages (new conversation in progress), do not overwrite them.
      if (messages.length > 0) return;
      didRestoreConversationRef.current = true;
      try {
        const conversation = await apiService.getAssistantConversation(conversationId);
        if (conversation.messages && conversation.messages.length > 0) {
          const loadedMessages: Message[] = conversation.messages
            // AC-1767: the concierge's auto-continue persists a hidden "continue"
            // driver as a user turn (flagged message_metadata.system_driven). Drop
            // those on reload so they never render as a fabricated user bubble —
            // the journey simply shows as having advanced, which is honest. Flag-
            // based, not string-matching "continue", so a real user typing
            // "continue" is unaffected.
            .filter((msg) => !(msg.role === 'user' && msg.message_metadata?.system_driven))
            .map((msg) => ({
              id: msg.message_id,
              role: msg.role as 'user' | 'assistant',
              content: msg.content,
              timestamp: new Date(msg.created_at),
            }));
          setMessages(loadedMessages);
        }
      } catch (error) {
        // AC: a stored id may be stale or foreign (e.g. 404 after the conversation
        // was deleted, or it belongs to another user). Clear it and start fresh
        // rather than crashing — the panel simply opens empty.
        console.error('Error loading conversation:', error);
        localStorage.removeItem(ASSISTANT_CONVERSATION_ID_KEY);
        setConversationId(null);
        // Allow a subsequent legitimately-resolved id to restore.
        didRestoreConversationRef.current = false;
      }
    };

    loadConversation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  // Load project context when projectId is available
  useEffect(() => {
    const loadOperationContext = async () => {
      if (projectId) {
        try {
          const context = await apiService.getOperationContext(projectId);
          setOperationContext(context);
        } catch (error) {
          console.error('Error loading project context:', error);
        }
      }
    };

    loadOperationContext();
  }, [projectId]);

  // Load workflow context when contextType is 'orchestration' and contextId is available
  useEffect(() => {
    const loadWorkflowContext = async () => {
      if (contextType === 'orchestration' && contextId) {
        try {
          const context = await apiService.getWorkflowContext(contextId);
          setWorkflowContext(context);
          // Panel stays collapsed by default (AC-1633): the data loads so the
          // "Show Workflow Details" toggle renders, but the user expands it.
        } catch (error) {
          console.error('Error loading workflow context:', error);
        }
      }
    };

    loadWorkflowContext();
  }, [contextType, contextId]);

  /**
   * Mark any prior assistant turn's still-pending preview as 'superseded'. Called
   * when a new user message is sent or a new preview arrives, so only the latest
   * plan is actionable. (AC-1646)
   */
  const supersedePriorPreviews = (list: Message[]): Message[] =>
    list.map((m) =>
      m.pendingActionPreview && m.pendingActionPreview.state === 'pending'
        ? { ...m, pendingActionPreview: { ...m.pendingActionPreview, state: 'superseded' } }
        : m
    );

  /**
   * AC-1757 one-shot auto-continue (TDD section 3.4 / decision #4): after a
   * SUCCESSFUL confirm whose journey is still OPEN (not complete, not blocked),
   * auto-send exactly ONE lightweight "continue" follow-up turn so the next
   * consequential card (e.g. the workflow preview after the Email is connected)
   * appears WITHOUT the user re-typing the goal. Gated per `keyMessageId` (the
   * confirmed turn's id) so it fires at most once per confirmed step — a per-step
   * guard mirroring AC-1747's initial-turn-only forcing; no force-loop. A
   * complete/blocked journey is rejected by `isJourneyOpen` and never continues.
   */
  const maybeAutoContinue = (
    journey: Journey | undefined,
    keyMessageId: number,
    hasPendingCard: boolean = false,
  ) => {
    // AC-1787 (defect fix): if this turn ALREADY surfaced a pending card the user
    // must act on (e.g. the build or schedule confirm card), NEVER auto-continue.
    // The journey bridges (AC-1776 gold-path / AC-1787 schedule) surface the next
    // step's card directly, so a hidden "continue" here is redundant AND harmful:
    // it produces a confusing extra turn and runs supersedePriorPreviews, which
    // flips the still-pending card to 'superseded' and DISABLES its confirm button.
    if (hasPendingCard) return;
    if (!isJourneyOpen(journey)) return;
    if (autoContinuedMessageIds.current.has(keyMessageId)) return;
    autoContinuedMessageIds.current.add(keyMessageId);
    // AC-1767: reuse the normal send path with `silent: true` so the model
    // re-enters the journey loop and surfaces the next step's card — but WITHOUT
    // fabricating a `role: 'user'` "continue" bubble. The "continue" string is
    // still sent to the backend (chatRequest.message) as a HIDDEN driver; only
    // the presentation changes (a centered system indicator, not a user turn).
    handleSendMessage('continue', { silent: true });
  };

  /**
   * Send a chat turn. `overrideMessage` lets the clarify chips auto-send the
   * chosen option as the next turn (a single action) without prefilling the
   * input — reusing this same send path instead of forking it. (AC-1645)
   *
   * AC-1767: `opts.silent` marks a SYSTEM-DRIVEN turn (the auto-continue). When
   * silent, the send path does NOT fabricate a `role: 'user'` bubble — it just
   * supersedes any stale preview and shows the centered "Continuing setup…"
   * indicator while the model responds. The message text (e.g. "continue") is
   * still sent to the backend as a hidden driver; the assistant's next-step card
   * appends normally. This is the core transparency fix: the journey advance is
   * never misattributed to the user.
   *
   * `opts.overrideContext` lets a caller bind THIS turn to a specific resource
   * regardless of the component's ambient contextType/contextId props. The
   * "Run it now" button (rendered after a workflow is built) uses this to bind
   * the run turn to the freshly created orchestration, because the
   * journey-complete turn is often in 'general' context but the backend
   * run_workflow dispatch binds the orchestration ONLY from
   * context_type==='orchestration' + context_id.
   */
  const handleSendMessage = async (
    overrideMessage?: string,
    opts?: {
      silent?: boolean;
      // AC: copy for the centered auto-continue indicator on a silent turn.
      // Defaults to "Continuing setup…" when unset (the journey auto-continue);
      // the "Run it now" button passes "Starting the run…" so the silent run
      // turn reads honestly instead of as a fabricated user message.
      silentIndicatorText?: string;
      overrideContext?: {
        context_type: 'orchestration' | 'project' | 'agent' | 'general';
        context_id: number;
      };
    }
  ) => {
    const messageText = overrideMessage ?? inputMessage;
    if (!messageText.trim() || isLoading) return;

    if (opts?.silent) {
      // System-driven turn: NO user bubble. Supersede any stale preview and
      // surface the transparent auto-continue indicator while the turn
      // round-trips, labelled with the caller's copy (or the default).
      setMessages(prev => supersedePriorPreviews(prev));
      setSilentIndicatorText(opts.silentIndicatorText ?? null);
      setIsAutoContinuing(true);
    } else {
      const userMessage: Message = {
        id: Date.now(),
        role: 'user',
        content: messageText,
        timestamp: new Date()
      };

      // A fresh user turn supersedes any earlier pending preview.
      setMessages(prev => [...supersedePriorPreviews(prev), userMessage]);
      setInputMessage('');
      // Collapse the auto-grown textarea back to its base height after sending.
      if (inputRef.current) inputRef.current.style.height = 'auto';
    }
    setIsLoading(true);

    try {
      const chatRequest: AssistantChatRequest = {
        message: messageText,
        conversation_id: conversationId || undefined,
        // AC: a per-call overrideContext (e.g. the "Run it now" button) wins over
        // the component's ambient context props so the run turn can bind to the
        // freshly built orchestration even from a 'general'-context turn.
        context_type: opts?.overrideContext?.context_type ?? contextType,
        context_id: opts?.overrideContext?.context_id ?? contextId,
        project_id: projectId,
        // AC-1767: flag the auto-continue as system-driven so the backend tags
        // the persisted user turn (message_metadata.system_driven=true). The
        // load mapper below filters those out, so a reloaded conversation never
        // reintroduces the fabricated "continue" user bubble.
        ...(opts?.silent ? { system_driven: true } : {})
      };

      const response: AssistantChatResponse = await apiService.chatWithAssistant(chatRequest);

      // Track conversation_id from backend response and update title
      if (!conversationId && response.conversation_id) {
        setConversationId(response.conversation_id);
        // Update conversation title with first user message
        const title = messageText.length > 50
          ? messageText.slice(0, 50).trim() + '...'
          : messageText;
        apiService.updateAssistantConversation(response.conversation_id, { title }).catch(() => {});
      }

      // Update context used from response
      if (response.context_used) {
        setContextUsed(response.context_used);
      }

      const assistantMessage: Message = {
        id: response.message_id,
        role: 'assistant',
        content: response.content,
        timestamp: new Date(),
        actions: response.actions,
        suggestedActions: response.suggested_actions,
        createdResources: resolveCreatedResources(response.actions || []),
        clarifyingQuestions: response.clarifying_questions,
        pendingActionPreview: response.pending_action_preview
          ? { ...response.pending_action_preview, state: 'pending' }
          : undefined,
        // AC-1757: carry the goal-journey envelope so the JourneyProgress strip
        // renders above this turn's pending-action card.
        journey: response.journey
      };

      // A new preview supersedes any earlier pending one.
      setMessages(prev => [...supersedePriorPreviews(prev), assistantMessage]);

      // AC-1701: salute the brand mascot when the copilot answers. The header
      // AnimatedLogo (and the sidebar logo) listen for this event.
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('agent-salute'));
      }

      // Check for project creation and show notification
      if (response.actions && response.actions.length > 0) {
        checkForOperationCreation(response.content, response.actions);
        
        // Dispatch events for assistant actions to trigger UI updates
        response.actions.forEach(action => {
          if (action.status === 'executed' && action.result?.success) {
            dispatchAssistantActionEvent(action);
          }
        });
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage: Message = {
        id: Date.now(),
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      // AC-1767: clear the transient auto-continue indicator. The next assistant
      // card has already been appended above, so the indicator is replaced by
      // real history rather than left lingering.
      setIsAutoContinuing(false);
      setSilentIndicatorText(null);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleActionClick = async (action: AssistantAction) => {
    try {
      const result = await apiService.executeAssistantAction(action.action_id);
      console.log('Action executed:', result);
    } catch (error) {
      console.error('Error executing action:', error);
    }
  };

  const handleSuggestedActionClick = (suggestedAction: string) => {
    setInputMessage(suggestedAction);
  };

  /**
   * A clarify chip was clicked: auto-send the chosen answer as the next chat
   * turn (single action), reusing the send path rather than prefilling the
   * input. (AC-1645)
   */
  const handleClarifyAnswer = (text: string) => {
    handleSendMessage(text);
  };

  /**
   * Set the per-message preview's lifecycle state (and optionally swap in a new
   * preview / banner). Scoped to the message that owns the card so a later turn
   * is never affected. (AC-1646)
   */
  const updatePreviewState = (
    messageId: number,
    patch: Partial<Pick<Message, 'pendingActionPreview' | 'planChanged'>>
  ) => {
    setMessages(prev =>
      prev.map(m => (m.id === messageId ? { ...m, ...patch } : m))
    );
  };

  /**
   * Confirm a plan preview: run the backend create, then surface the executed
   * resources via the existing persistent "Open <resource>" control. A 409 comes
   * back as a fresh superseded preview (apiService returns the body, AC-1644):
   * swap the card to the new plan and show a "plan changed" banner. On error,
   * re-enable the card. (AC-1646)
   */
  const handleConfirmPreview = async (
    message: Message,
    preview: PendingActionPreview & { state: PreviewState }
  ) => {
    if (!conversationId) return;

    // AC-1785 (epic AC-1778): a run preview confirm follows the same spine
    // (confirm the pending action) but then TRIGGERS the run from the client and
    // surfaces a live CompactRunProgressCard instead of an "Open <resource>"
    // control. Route it to the dedicated run handler.
    if (preview.action_type === 'preview_run_orchestration') {
      return handleConfirmRun(message, preview);
    }

    updatePreviewState(message.id, {
      pendingActionPreview: { ...preview, state: 'creating' }
    });

    try {
      const response = await apiService.confirmAssistantAction(
        conversationId,
        preview.action_id
      );

      // AC-1787 (defect fix): distinguish a REAL 409 (the plan was superseded
      // under the user) from a SUCCESS turn that legitimately carries a NEW
      // pending_action_preview. The journey's build-confirm returns HTTP 200 with
      // executed actions AND a fresh schedule card (the next concierge step) —
      // that is NOT a conflict and must not show the "plan changed" banner. Only
      // the apiService-stamped `planChanged` marker (set solely on the 409 branch)
      // — or a body with neither a message_id nor any actions (a stale-action
      // conflict that comes back as a bare {detail}) — drives the superseded path.
      const isSuperseded =
        response.planChanged === true ||
        (response.message_id == null &&
          !(response.actions && response.actions.length > 0));

      if (isSuperseded) {
        // Superseded: swap in the fresh plan (if the backend sent one) and flag
        // the banner; otherwise revert THIS card to a retryable 'pending'.
        updatePreviewState(message.id, {
          pendingActionPreview: response.pending_action_preview
            ? { ...response.pending_action_preview, state: 'pending' }
            : { ...preview, state: 'pending' },
          planChanged: true
        });
        return;
      }

      // Success: mark created and append the follow-up turn so the existing
      // resolveCreatedResources builds the "Open <resource>" control.
      updatePreviewState(message.id, {
        pendingActionPreview: { ...preview, state: 'created' }
      });

      const followUp: Message = {
        id: response.message_id,
        role: 'assistant',
        content: response.content,
        timestamp: new Date(),
        actions: response.actions,
        suggestedActions: response.suggested_actions,
        createdResources: resolveCreatedResources(response.actions || []),
        // AC-1787 (defect fix): a SUCCESS turn may legitimately carry the journey's
        // NEXT-step card (the schedule card auto-surfaced after a build confirm).
        // Surface it on this follow-up turn in a clean 'pending' state — NO
        // planChanged banner — so the user sees the schedule card and can confirm
        // it inline. A confirm turn with no next-step preview leaves this undefined.
        pendingActionPreview: response.pending_action_preview
          ? { ...response.pending_action_preview, state: 'pending' }
          : undefined,
        // AC-1757: the build-workflow confirm carries the (likely complete)
        // journey envelope — surface it so the JourneyProgress strip shows the
        // all-set state. A complete journey is not open, so no auto-continue.
        journey: response.journey
      };
      setMessages(prev => [...prev, followUp]);

      maybeAutoContinue(
        response.journey,
        response.message_id,
        !!response.pending_action_preview,
      );

      if (response.actions && response.actions.length > 0) {
        checkForOperationCreation(response.content, response.actions);
        response.actions.forEach(action => {
          if (action.status === 'executed' && action.result?.success) {
            dispatchAssistantActionEvent(action);
          }
        });
      }
    } catch (error) {
      console.error('Error confirming action:', error);
      // Re-enable the card so the user can retry.
      updatePreviewState(message.id, {
        pendingActionPreview: { ...preview, state: 'pending' }
      });
    }
  };

  /**
   * Confirm a RUN preview (AC-1785, epic AC-1778): authorize the run, then
   * TRIGGER it from the client and show live status in chat.
   *
   * Spine reuse: `confirmAssistantAction` replays the pending
   * `preview_run_orchestration` server-side. The confirm does only the cheap,
   * synchronous part — it pre-creates a RunInstance (status pending) and records
   * an executed `confirm_run_orchestration` action whose result carries
   * `{orchestration_id, run_instance_id}`. It does NOT block on the run.
   *
   * The client then fires the EXISTING `apiService.runWorkflow(orchId, runId)`
   * (passing the pre-created run instance id so the run reuses it) and appends a
   * follow-up turn carrying a `runProgress` handle. That turn renders a
   * CompactRunProgressCard which subscribes to `useExecution(orchId)` and shows
   * live status off the SSE-driven cache. On confirm error the card returns to
   * pending so the user can retry.
   */
  const handleConfirmRun = async (
    message: Message,
    preview: PendingActionPreview & { state: PreviewState }
  ) => {
    if (!conversationId) return;
    updatePreviewState(message.id, {
      pendingActionPreview: { ...preview, state: 'creating' }
    });

    try {
      const response = await apiService.confirmAssistantAction(
        conversationId,
        preview.action_id
      );

      // AC-1787 (defect fix): only a REAL 409 (apiService-stamped `planChanged`)
      // is superseded. A success turn carrying a fresh pending_action_preview is a
      // legitimate next-step card, not a conflict. Swap in the fresh plan + show
      // the banner only on a genuine 409.
      if (response.planChanged === true) {
        updatePreviewState(message.id, {
          pendingActionPreview: response.pending_action_preview
            ? { ...response.pending_action_preview, state: 'pending' }
            : { ...preview, state: 'pending' },
          planChanged: true
        });
        return;
      }

      // Locate the executed confirm_run_orchestration action carrying the run
      // ids. A 409 conflict comes back without it — treat that as a retryable
      // plan-changed rather than masking it as a started run.
      const runAction = (response.actions || []).find(
        (a) =>
          a.action_type === 'confirm_run_orchestration' &&
          a.status === 'executed' &&
          a.result?.run_instance_id != null
      );

      if (!runAction) {
        updatePreviewState(message.id, {
          pendingActionPreview: { ...preview, state: 'pending' },
          planChanged: true
        });
        return;
      }

      const result = runAction.result as Record<string, any>;
      const orchestrationId = Number(result.orchestration_id);
      const runInstanceId = Number(result.run_instance_id);
      const workflowName: string | undefined =
        result.orchestration_name ?? preview.workflow_name;
      // The run belongs to the workflow currently open in this panel, so the
      // owning project is the chat's project context (the result may also echo
      // it). Used only for the Open-workflow deep link.
      const runProjectId: number | undefined =
        result.project_id != null ? Number(result.project_id) : projectId;
      // AC-1778: the optional input_message override the user confirmed travels
      // on the confirm result; forward it so the run actually uses it (not just
      // the preview echo).
      const inputMessage: string | undefined =
        result.input_message ?? preview.input_message ?? undefined;

      // Mark the preview card resolved (reads "Running").
      updatePreviewState(message.id, {
        pendingActionPreview: { ...preview, state: 'created' }
      });

      // Append the assistant follow-up turn that carries the live-run card.
      const followUp: Message = {
        id: response.message_id ?? Date.now(),
        role: 'assistant',
        content: response.content,
        timestamp: new Date(),
        actions: response.actions,
        suggestedActions: response.suggested_actions,
        journey: response.journey,
        runProgress: {
          orchestrationId,
          runInstanceId,
          stepCount: preview.step_count,
          projectId: runProjectId,
          workflowName
        }
      };
      setMessages(prev => [...prev, followUp]);

      // Trigger the run from the client, reusing the pre-created run instance.
      // Non-blocking from the UI's perspective: progress streams into the card
      // via the parallel useExecution / SSE subscription. A trigger failure is
      // surfaced to the console; the card still shows the pending run and the
      // Open-workflow link remains reachable.
      try {
        await apiService.runWorkflow(
          String(orchestrationId),
          String(runInstanceId),
          inputMessage,
        );
      } catch (runError) {
        console.error('Error triggering workflow run:', runError);
      }
    } catch (error) {
      console.error('Error confirming run:', error);
      updatePreviewState(message.id, {
        pendingActionPreview: { ...preview, state: 'pending' }
      });
    }
  };

  /**
   * Adjust a plan: prefill the input with a step-indexed hint and focus it so the
   * user can describe the change, driving a re-plan loop. (AC-1646)
   */
  const handleAdjustPreview = (preview: PendingActionPreview & { state: PreviewState }) => {
    const firstStep = preview.steps[0]?.order ?? 1;
    setInputMessage(`Adjust step ${firstStep}: `);
    inputRef.current?.focus();
  };

  /**
   * Decline a plan: tell the backend nothing should be created and dim the card.
   * (AC-1646)
   */
  const handleDeclinePreview = async (
    message: Message,
    preview: PendingActionPreview & { state: PreviewState }
  ) => {
    if (conversationId) {
      try {
        await apiService.declineAssistantAction(conversationId, preview.action_id);
      } catch (error) {
        console.error('Error declining action:', error);
      }
    }
    updatePreviewState(message.id, {
      pendingActionPreview: { ...preview, state: 'declined' }
    });
  };

  /**
   * Approve an MCP proposal (AC-1738): provision the remote server with the
   * chosen scope + credentials. Reuses the propose-before-commit spine — the
   * confirm replays the pending action server-side, the mutation invalidates the
   * MCP servers query, and the executed provision_mcp_server action flows through
   * resolveCreatedResources-style follow-up. Default scope is project, so we fill
   * in the owning project_id when the user kept that scope. Credentials are sent
   * ONLY on this request (never logged). On failure the card returns to pending.
   */
  const handleApproveMcp = async (
    message: Message,
    preview: McpProposalPreview & { state: PreviewState },
    req: McpProvisionRequest
  ) => {
    if (!conversationId) return;
    updatePreviewState(message.id, {
      pendingActionPreview: { ...preview, state: 'provisioning' }
    });

    const request: McpProvisionRequest = {
      ...req,
      project_id:
        req.scope === 'project' ? req.project_id ?? projectId : undefined
    };

    try {
      const response = await confirmMcpProposal.mutateAsync({
        conversationId,
        actionId: preview.action_id,
        request
      });

      updatePreviewState(message.id, {
        pendingActionPreview: { ...preview, state: 'provisioned' }
      });

      if (response.message_id != null) {
        const followUp: Message = {
          id: response.message_id,
          role: 'assistant',
          content: response.content,
          timestamp: new Date(),
          actions: response.actions,
          suggestedActions: response.suggested_actions,
          // AC-1740: surface an "Open Integrations" control built from the
          // executed provision_mcp_server action so the user can jump straight
          // to the page where the newly provisioned server now appears.
          createdResources: resolveCreatedResources(response.actions || [])
        };
        setMessages(prev => [...prev, followUp]);
      }
    } catch (error) {
      console.error('Error provisioning MCP server:', error);
      updatePreviewState(message.id, {
        pendingActionPreview: { ...preview, state: 'pending' }
      });
    }
  };

  /**
   * Decline an MCP proposal (AC-1738): decline-without-residue — nothing is
   * provisioned. Dim the card and tell the backend to mark the action declined.
   */
  const handleDeclineMcp = async (
    message: Message,
    preview: McpProposalPreview & { state: PreviewState }
  ) => {
    if (conversationId) {
      try {
        await declineMcpProposal.mutateAsync({
          conversationId,
          actionId: preview.action_id
        });
      } catch (error) {
        console.error('Error declining MCP proposal:', error);
      }
    }
    updatePreviewState(message.id, {
      pendingActionPreview: { ...preview, state: 'declined' }
    });
  };

  /**
   * Connect an unconfigured built-in from inside chat (AC-1746, defect AC-1743):
   * save the built-in's credentials (e.g. Email) so the user does NOT have to
   * leave the assistant. Reuses the propose-before-commit spine — the confirm
   * replays the pending `configure_builtin_mcp` action server-side, the mutation
   * invalidates the built-in credential status + MCP servers queries, and on
   * success the original request becomes satisfiable via Tier-1 reuse. Credentials
   * are sent ONLY on this request (never logged). On failure the card returns to
   * pending so the user can retry.
   */
  const handleConfirmBuiltin = async (
    message: Message,
    preview: McpConfigureBuiltinPreview & { state: PreviewState },
    req: McpConfigureBuiltinRequest
  ) => {
    if (!conversationId) return;
    updatePreviewState(message.id, {
      pendingActionPreview: { ...preview, state: 'configuring' }
    });

    try {
      const response = await confirmBuiltinSetup.mutateAsync({
        conversationId,
        actionId: preview.action_id,
        request: req
      });

      updatePreviewState(message.id, {
        pendingActionPreview: { ...preview, state: 'configured' }
      });

      // AC-1757: the confirm response carries the advanced journey envelope (Email
      // now done; the build step current — or `complete`/`blocked`). Surface a
      // follow-up assistant turn so its JourneyProgress strip updates, then
      // auto-continue ONCE if the journey is still open so the next consequential
      // card (the workflow preview) appears without the user re-typing.
      const followUpId = response.message_id ?? Date.now();
      const followUp: Message = {
        id: followUpId,
        role: 'assistant',
        content: response.content,
        timestamp: new Date(),
        actions: response.actions,
        suggestedActions: response.suggested_actions,
        createdResources: resolveCreatedResources(response.actions || []),
        // AC-1787 (defect fix): the Email-connect confirm now surfaces the NEXT
        // step's card DIRECTLY (the AC-1776 gold-path build preview) on its own
        // response. Render it here instead of relying on the auto-continue turn
        // (which is correctly suppressed when a card is already present) -- else
        // the build card never appears and the workflow never gets created.
        pendingActionPreview: response.pending_action_preview
          ? { ...response.pending_action_preview, state: 'pending' }
          : undefined,
        journey: response.journey
      };
      setMessages(prev => [...prev, followUp]);

      maybeAutoContinue(
        response.journey,
        followUpId,
        !!response.pending_action_preview,
      );
    } catch (error) {
      console.error('Error connecting built-in:', error);
      updatePreviewState(message.id, {
        pendingActionPreview: { ...preview, state: 'pending' }
      });
    }
  };

  /**
   * Decline an in-chat built-in configure card (AC-1746): decline-without-residue
   * — nothing is saved. Dim the card and tell the backend to mark the action
   * declined.
   */
  const handleDeclineBuiltin = async (
    message: Message,
    preview: McpConfigureBuiltinPreview & { state: PreviewState }
  ) => {
    let declinedJourney: Journey | undefined;
    if (conversationId) {
      try {
        const response = await declineBuiltinSetup.mutateAsync({
          conversationId,
          actionId: preview.action_id
        });
        // AC-1757: a journey decline re-derives as BLOCKED. Capture the journey
        // so the JourneyProgress strip on THIS turn surfaces the plain-language
        // stop reason. We attach it to the declined message rather than appending
        // a new turn, so the blocked reason renders once (in the strip), not
        // duplicated as a content bubble. A blocked journey is NOT open, so the
        // decline path NEVER auto-continues.
        declinedJourney = response?.journey;
      } catch (error) {
        console.error('Error declining built-in setup:', error);
      }
    }
    updatePreviewState(message.id, {
      pendingActionPreview: { ...preview, state: 'declined' }
    });
    if (declinedJourney) {
      setMessages(prev =>
        prev.map(m => (m.id === message.id ? { ...m, journey: declinedJourney } : m))
      );
    }
  };

  /**
   * Confirm the FINAL concierge step (AC-1790, epic AC-1787): create a recurring
   * schedule for the just-built email-summary workflow WITHOUT leaving chat.
   * Reuses the propose-before-commit spine — the confirm replays the pending
   * `configure_schedule` action server-side, the mutation invalidates the
   * workflow's schedule query, and on success the journey re-derives complete (or
   * surfaces a graceful failure message, e.g. an AC-505 queue_read-first rejection
   * comes back as a non-error reply). The card's adjusted cadence is sent ONLY on
   * this request. On a thrown failure the card returns to pending so the user can
   * retry.
   */
  const handleConfirmSchedule = async (
    message: Message,
    preview: ScheduleConfirmPreview & { state: PreviewState },
    req: ScheduleConfirmRequest
  ) => {
    if (!conversationId) return;
    updatePreviewState(message.id, {
      pendingActionPreview: { ...preview, state: 'confirming' }
    });

    try {
      const response = await confirmScheduleSetup.mutateAsync({
        conversationId,
        actionId: preview.action_id,
        request: req
      });

      updatePreviewState(message.id, {
        pendingActionPreview: { ...preview, state: 'confirmed' }
      });

      // The confirm response carries the advanced journey envelope (schedule now
      // done -> journey complete, or a graceful blocked/failure reply). Surface a
      // follow-up assistant turn so its JourneyProgress strip updates, then
      // auto-continue ONCE if the journey is somehow still open.
      const followUpId = response.message_id ?? Date.now();
      const followUp: Message = {
        id: followUpId,
        role: 'assistant',
        content: response.content,
        timestamp: new Date(),
        actions: response.actions,
        suggestedActions: response.suggested_actions,
        createdResources: resolveCreatedResources(response.actions || []),
        journey: response.journey
      };
      setMessages(prev => [...prev, followUp]);

      // AC-1787 (defect fix): the schedule-confirm is the journey's FINAL create
      // step. Refresh the dashboard's data sources (RQ workflows list + Zustand
      // project list) so the Command Center reflects the just-scheduled workflow
      // without a manual refresh — mirrors the create/run confirm paths.
      void refreshAfterAssistantCreate(response.actions || []);

      maybeAutoContinue(
        response.journey,
        followUpId,
        !!response.pending_action_preview,
      );
    } catch (error) {
      console.error('Error setting up schedule:', error);
      updatePreviewState(message.id, {
        pendingActionPreview: { ...preview, state: 'pending' }
      });
    }
  };

  /**
   * Decline the in-chat schedule-setup card (AC-1790): decline-without-residue —
   * no schedule is created. Dim the card and tell the backend to mark the action
   * declined; a journey decline re-derives as BLOCKED, so we attach the returned
   * journey to THIS turn (the stop reason renders once in the strip) and never
   * auto-continue.
   */
  const handleDeclineSchedule = async (
    message: Message,
    preview: ScheduleConfirmPreview & { state: PreviewState }
  ) => {
    let declinedJourney: Journey | undefined;
    if (conversationId) {
      try {
        const response = await declineScheduleSetup.mutateAsync({
          conversationId,
          actionId: preview.action_id,
          orchestrationId: preview.orchestration_id
        });
        declinedJourney = response?.journey;
      } catch (error) {
        console.error('Error declining schedule setup:', error);
      }
    }
    updatePreviewState(message.id, {
      pendingActionPreview: { ...preview, state: 'declined' }
    });
    if (declinedJourney) {
      setMessages(prev =>
        prev.map(m => (m.id === message.id ? { ...m, journey: declinedJourney } : m))
      );
    }
  };

  /**
   * Open a resource the assistant just created. User-initiated (so no WCAG
   * change-of-context obligation) and built from the structured project id, so
   * the link is always valid. We refresh the project list and select the owning
   * project before navigating — mirroring ProjectsPage — so the destination page
   * mounts with currentProject set instead of flashing an empty state. getState()
   * is read AFTER the await so a just-created project is present.
   */
  const handleOpenCreatedResource = async (resource: CreatedResource) => {
    try {
      await loadProjects();
    } catch {
      // Non-fatal: navigation below still works; useProjectContext backfills
      // currentProject from the URL on the destination page.
    }
    const project = useProjectStore
      .getState()
      .projects.find((p) => String(p.project_id) === String(resource.projectId));
    if (project) {
      setCurrentOperation(project);
    }
    if (resource.type === 'workflow') {
      // AC-1634: signal the editor to run a one-time compact auto-layout so a
      // freshly-created workflow opens tidy instead of with overlapping nodes.
      navigate(resource.navigateTo, { state: { autoLayout: true } });
    } else {
      navigate(resource.navigateTo);
    }
  };

  const handleKnowledgeSelect = (knowledge: KnowledgeSearchResult) => {
    setInputMessage(`Tell me more about ${knowledge.name}`);
  };

  const dispatchAssistantActionEvent = (action: AssistantAction) => {
    // Dispatch custom event to notify other components about assistant actions
    const event = new CustomEvent('assistant-action', {
      detail: {
        action: action.action_type,
        projectId: action.result?.project_id,
        agentId: action.result?.agent_id,
        workflowId: action.result?.orchestration_id,
        success: action.result?.success
      }
    });
    window.dispatchEvent(event);
  };

  /**
   * AC-1787 (defect fix): after the assistant creates/changes a resource, the
   * Command Center reads BOTH Zustand `projects` (the "Total Projects" count) and
   * the React Query `queryKeys.workflows()` list. Neither is touched by the
   * assistant create paths, so the dashboard stays at "0 projects / No projects
   * yet" until a manual refresh. This central hook closes that gap by invalidating
   * the relevant RQ keys AND robustly refreshing the Zustand project list.
   *
   * Follows the repo's RQ conventions (see useProjectMutations.ts): each write
   * declares its invalidation set explicitly. projects()+workflows() are the
   * priority; per-project agent/model keys are invalidated too when those
   * resources were created in the same turn.
   */
  const refreshAfterAssistantCreate = async (actions: AssistantAction[]) => {
    const executed = actions.filter(
      (a) => a.status === 'executed' && a.result?.success,
    );
    if (executed.length === 0) return;

    const has = (type: string) => executed.some((a) => a.action_type === type);
    // A new project OR workflow OR schedule OR run all change what the dashboard
    // shows (project count, workflows list, schedule indicators).
    const projectTouched = has('create_project');
    const workflowTouched =
      has('create_simple_orchestration') ||
      has('configure_schedule') ||
      has('confirm_run_orchestration');

    if (projectTouched) {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects() });
    }
    if (workflowTouched || projectTouched) {
      // Invalidate the cross-project workflows list (Command Center reads it via
      // queryKeys.workflows()). A new project can carry a workflow too, so refresh
      // on either.
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows() });
    }

    // Per-project agent list, when an agent was created this turn. (Model
    // creation is not surfaced as a standalone assistant action — models are
    // created as part of a workflow build — so there is no create_model branch.)
    for (const a of executed) {
      const pid = a.result?.project_id;
      if (pid == null) continue;
      if (a.action_type === 'create_agent') {
        queryClient.invalidateQueries({
          queryKey: queryKeys.projectAgents(pid),
        });
      }
    }

    // Robustly refresh the Zustand project list so the dashboard's
    // `projects.length` count (and the workflows-list project lookups) reflect the
    // new state immediately. Awaited + guarded so a transient failure never throws
    // out of the post-create flow.
    await loadProjects().catch(() => {
      /* non-fatal: RQ invalidation above still refreshes the workflows list */
    });
  };

  const checkForOperationCreation = (content: string, actions: AssistantAction[]) => {
    // AC-1787: refresh the dashboard's data sources (RQ workflows list + Zustand
    // project list) on ANY successful create/run/schedule in this turn, regardless
    // of which notification branch below fires. Fire-and-forget: the awaited
    // refresh runs in the background; the synchronous notification logic continues.
    void refreshAfterAssistantCreate(actions);

    // Check if any action indicates project creation
    const operationCreationAction = actions.find(action =>
      action.action_type === 'create_project' &&
      action.status === 'executed' &&
      action.result?.success
    );

    if (operationCreationAction) {
      const projectName = operationCreationAction.result?.project_name || 'New Project';
      const projectId = operationCreationAction.result?.project_id;

      // Store the new project ID for navigation
      setNewOperationId(projectId);

      // Show success notification
      setSuccessMessage(`✅ Project "${projectName}" created successfully!`);
      setShowSuccessNotification(true);

      // Auto-hide notification after 8 seconds
      setTimeout(() => {
        setShowSuccessNotification(false);
        setNewOperationId(null);
      }, 8000);

      return true;
    }
    
    // Check for workflow creation
    const workflowCreationAction = actions.find(action => 
      action.action_type === 'create_simple_orchestration' && 
      action.status === 'executed' &&
      action.result?.success
    );

    if (workflowCreationAction) {
      const workflowName = workflowCreationAction.result?.orchestration_name || 'New Workflow';
      const workflowId = workflowCreationAction.result?.orchestration_id;
      
      // Show success notification
      setSuccessMessage(`✅ Workflow "${workflowName}" created successfully!`);
      setShowSuccessNotification(true);
      
      // Auto-hide notification after 6 seconds
      setTimeout(() => {
        setShowSuccessNotification(false);
      }, 6000);
      
      return true;
    }
    
    // Check for agent creation
    const agentCreationAction = actions.find(action => 
      action.action_type === 'create_agent' && 
      action.status === 'executed' &&
      action.result?.success
    );

    if (agentCreationAction) {
      const agentName = agentCreationAction.result?.agent_name || 'New Agent';
      const agentId = agentCreationAction.result?.agent_id;
      
      // Show success notification
      setSuccessMessage(`✅ Agent "${agentName}" created successfully!`);
      setShowSuccessNotification(true);
      
      // Auto-hide notification after 6 seconds
      setTimeout(() => {
        setShowSuccessNotification(false);
      }, 6000);
      
      return true;
    }
    
    return false;
  };

  const processMessageContent = (content: string) => {
    try {
      const result = marked(content);
      return { __html: typeof result === 'string' ? result : result.toString() };
    } catch (error) {
      // Fallback to plain text if markdown parsing fails
      return { __html: content };
    }
  };

  const renderOperationContext = () => {
    if (!projectContext || !showOperationContext) return null;

    return (
      <div className="bg-primary-50 dark:bg-blue-900/20 border border-primary-200 dark:border-blue-800 rounded-lg p-4 mb-4">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-200">Project Context</h3>
          <button
            onClick={() => setShowOperationContext(false)}
            className="text-primary-600 hover:text-primary-800"
          >
            Hide
          </button>
        </div>
        
        <div className="space-y-2">
          <div>
            <span className="font-medium">Project:</span> {projectContext.name}
          </div>
          {projectContext.description && (
            <div>
              <span className="font-medium">Description:</span> {projectContext.description}
            </div>
          )}
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="font-medium">Agents:</span> {projectContext.agent_count}
            </div>
            <div>
              <span className="font-medium">Workflows:</span> {projectContext.orchestration_count}
            </div>
            <div>
              <span className="font-medium">Schemas:</span> {projectContext.model_count}
            </div>
          </div>
          
          
          {projectContext.recent_orchestrations.length > 0 && (
            <div>
              <span className="font-medium">Recent Workflows:</span>
              <div className="mt-1 space-y-1">
                {projectContext.recent_orchestrations.slice(0, 3).map((orch: any, index: number) => (
                  <div key={index} className="text-sm text-gray-600 dark:text-gray-400">
                    • {orch.name} ({orch.step_count} steps)
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderWorkflowContext = () => {
    if (!workflowContext || !showWorkflowContext) return null;

    return (
      <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4 mb-4">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-semibold text-purple-900 dark:text-purple-200">Current Workflow</h3>
          <button
            onClick={() => setShowWorkflowContext(false)}
            className="text-purple-600 hover:text-purple-800"
          >
            Hide
          </button>
        </div>
        
        <div className="space-y-3">
          <div>
            <span className="font-medium">Name:</span> {workflowContext.name}
          </div>
          {workflowContext.description !== "No description provided" && (
            <div>
              <span className="font-medium">Description:</span> {workflowContext.description}
            </div>
          )}
          
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium">Total Steps:</span> {workflowContext.total_steps}
            </div>
            <div>
              <span className="font-medium">Agents Used:</span> {workflowContext.agents_used_count}
            </div>
            <div>
              <span className="font-medium">Success Rate:</span> {workflowContext.execution_stats.success_rate}%
            </div>
            <div>
              <span className="font-medium">Total Runs:</span> {workflowContext.execution_stats.total_runs}
            </div>
          </div>

          {/* Step Types */}
          {Object.keys(workflowContext.step_types).length > 0 && (
            <div>
              <span className="font-medium">Step Types:</span>
              <div className="flex flex-wrap gap-2 mt-1">
                {Object.entries(workflowContext.step_types).map(([type, count]) => (
                  <span key={type} className="bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300 px-2 py-1 rounded text-xs">
                    {type.replace('_', ' ')}: {count}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Recent Run Status */}
          {workflowContext.most_recent_run && (
            <div>
              <span className="font-medium">Latest Run:</span>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  workflowContext.most_recent_run.status === 'COMPLETED' 
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                    : workflowContext.most_recent_run.status === 'FAILED'
                    ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                    : workflowContext.most_recent_run.status === 'RUNNING'
                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                    : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                }`}>
                  {workflowContext.most_recent_run.status}
                </span>
                <span className="ml-2">{workflowContext.most_recent_run.created_at}</span>
              </div>
            </div>
          )}
          
          {/* Workflow Steps Summary */}
          {workflowContext.steps.length > 0 && (
            <div>
              <span className="font-medium">Workflow Steps:</span>
              <div className="mt-2 max-h-32 overflow-y-auto">
                {workflowContext.steps.slice(0, 5).map((step, index) => (
                  <div key={step.step_id} className="text-sm text-gray-600 dark:text-gray-400 py-1">
                    <span className="font-medium">{step.step_number}.</span> {step.label} 
                    <span className="text-gray-500 dark:text-gray-400"> ({step.type})</span>
                    {step.agent_name && <span className="text-purple-600"> - {step.agent_name}</span>}
                  </div>
                ))}
                {workflowContext.steps.length > 5 && (
                  <div className="text-sm text-gray-500 dark:text-gray-400 py-1">
                    ... and {workflowContext.steps.length - 5} more steps
                  </div>
                )}
              </div>
            </div>
          )}

          {/* AI-Generated Summary */}
          <div className="bg-purple-100 dark:bg-purple-900/30 p-3 rounded-lg">
            <span className="font-medium text-purple-900 dark:text-purple-200">AI Summary:</span>
            <div className="text-sm text-purple-800 dark:text-purple-300 mt-1">
              {workflowContext.summary}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderKnowledgeContext = () => {
    if (contextUsed.length === 0) return null;

    return (
      <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-4">
        <h3 className="text-lg font-semibold text-green-900 dark:text-green-200 mb-3">Knowledge Used</h3>
        <div className="space-y-2">
          {contextUsed.map((knowledge, index) => (
            <div key={index} className="text-sm">
              <div className="flex items-center gap-2">
                <span className="font-medium text-green-800 dark:text-green-300">{knowledge.content_type}:</span>
                <span className="text-green-700 dark:text-green-400">{knowledge.name}</span>
                <span className="text-xs text-green-600 dark:text-green-500">({(knowledge.similarity_score * 100).toFixed(0)}% match)</span>
              </div>
              <div className="text-green-600 dark:text-green-500 mt-1">
                {knowledge.content_text.substring(0, 150)}...
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* AC-1616: compact assistant-model selector. Reuses ModelPickerField
          with the assistant allow-list (mid-tier+) injected via llmCatalog so
          only allowed models render. Picking a row persists the preference. */}
      {assistantModels?.models && assistantModels.models.length > 0 && (
        <div className="border-b border-gray-200 dark:border-gray-700 px-4 py-2">
          <div className="max-w-xs">
            <ModelPickerField
              value={selectedAssistantModel}
              onChange={(id) => setAssistantModelPreference.mutate(id)}
              llmCatalog={assistantModels.models}
              label="Assistant model"
              helpText=""
              disabled={setAssistantModelPreference.isPending}
              testId="assistant-model-picker"
            />
          </div>
        </div>
      )}

      {/* Success Notification */}
      {showSuccessNotification && (
        <div
          role="status"
          aria-live="polite"
          className="fixed top-4 right-4 z-50 bg-green-500 text-white px-6 py-4 rounded-lg shadow-lg max-w-sm"
        >
          <div className="flex items-center space-x-2 mb-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="font-medium">{successMessage}</span>
            <button
              onClick={() => setShowSuccessNotification(false)}
              aria-label="Dismiss notification"
              className="ml-auto text-white hover:text-green-100"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {newOperationId && (
            <div className="flex space-x-2">
              <button
                onClick={() => {
                  navigate(`/projects/${newOperationId}`);
                  setShowSuccessNotification(false);
                }}
                className="bg-white dark:bg-cap-dark text-green-600 px-3 py-1 rounded text-sm font-medium hover:bg-green-50 transition-colors"
              >
                View Project
              </button>
              <button
                onClick={() => setShowSuccessNotification(false)}
                className="text-green-100 hover:text-white text-sm"
              >
                Dismiss
              </button>
            </div>
          )}
        </div>
      )}
      
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Show workflow context if available */}
        {workflowContext && (
          <div className="mb-4">
            <button
              onClick={() => setShowWorkflowContext(!showWorkflowContext)}
              className="text-sm text-purple-600 hover:text-purple-800 flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              {showWorkflowContext ? 'Hide' : 'Show'} Workflow Details
            </button>
            {renderWorkflowContext()}
          </div>
        )}

        {/* Show project context if available and not showing workflow context */}
        {projectContext && !workflowContext && (
          <div className="mb-4">
            <button
              onClick={() => setShowOperationContext(!showOperationContext)}
              className="text-sm text-primary-600 hover:text-primary-800 flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {showOperationContext ? 'Hide' : 'Show'} Project Context
            </button>
            {renderOperationContext()}
          </div>
        )}

        {messages.length === 0 && (
          <div className="text-center text-gray-500 dark:text-gray-400 py-8">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <p className="text-lg font-medium text-gray-900 dark:text-warm-white">How can I help you today?</p>
            {workflowContext ? (
              <p className="text-sm">I can see you're working with <span className="font-medium text-purple-600">{workflowContext.name}</span>. Ask me anything about this workflow!</p>
            ) : (
              <p className="text-sm">Ask me about your project, workflows, agents, or anything else!</p>
            )}
            
            {/* Show workflow context if available */}
            {workflowContext && (
              <div className="mt-4 text-left">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Current workflow: <span className="font-medium text-purple-600">{workflowContext.name}</span></p>
                <div className="flex gap-2 text-xs">
                  <span className="bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300 px-2 py-1 rounded">
                    {workflowContext.total_steps} steps
                  </span>
                  <span className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-1 rounded">
                    {workflowContext.agents_used_count} agents
                  </span>
                  <span className={`px-2 py-1 rounded ${
                    workflowContext.execution_stats.success_rate >= 80
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                      : workflowContext.execution_stats.success_rate >= 50
                      ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                      : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                  }`}>
                    {workflowContext.execution_stats.success_rate}% success
                  </span>
                </div>
              </div>
            )}

            {/* Show project context if no workflow context */}
            {projectContext && !workflowContext && (
              <div className="mt-4 text-left">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Current project: <span className="font-medium">{projectContext.name}</span></p>
                <div className="flex gap-2 text-xs">
                  <span className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-1 rounded">
                    {projectContext.agent_count} agents
                  </span>
                  <span className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 px-2 py-1 rounded">
                    {projectContext.orchestration_count} workflows
                  </span>
                  <span className="bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300 px-2 py-1 rounded">
                    {projectContext.model_count} schemas
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start items-start gap-2'}`}
          >
              {/* AC-1701: brand the copilot's replies with the Agent Commander
                  mascot. A static (img) variant is used per-message to keep long
                  transcripts cheap — the animated mascot lives only in the header.
                  User rows stay avatar-less and right-aligned. */}
              {message.role === 'assistant' && (
                <Logo
                  variant="static-light"
                  size={28}
                  themeAware
                  className="flex-shrink-0 mt-0.5"
                />
              )}
              <div
                className={`max-w-[85%] px-4 py-2 rounded-lg ${
                  message.role === 'user'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-warm-white'
                }`}
              >
                <div
                  className={`${message.role === 'user' ? 'text-white' : 'text-gray-900 dark:text-warm-white'} prose prose-sm max-w-none [&_h1]:text-inherit [&_h2]:text-inherit [&_h3]:text-inherit [&_h4]:text-inherit [&_p]:text-inherit [&_strong]:text-inherit [&_b]:text-inherit [&_em]:text-inherit [&_code]:text-inherit [&_pre]:text-inherit [&_blockquote]:text-inherit [&_ul]:text-inherit [&_ol]:text-inherit [&_li]:text-inherit [&_a]:text-blue-400`}
                  dangerouslySetInnerHTML={processMessageContent(message.content)}
                />
              
              {/* Show knowledge context for assistant messages */}
              {message.role === 'assistant' && contextUsed.length > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Knowledge used:</div>
                  {contextUsed.slice(0, 2).map((knowledge, index) => (
                    <div key={index} className="text-xs text-gray-600 dark:text-gray-400">
                      • {knowledge.name} ({(knowledge.similarity_score * 100).toFixed(0)}% match)
                    </div>
                  ))}
                </div>
              )}
              
              {/* Persistent "Open <Name>" control for resources the assistant
                  just created. Stays in the transcript (unlike the success
                  toast) and is user-initiated. (AC-1622) */}
              {message.role === 'assistant' && message.createdResources && message.createdResources.length > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex flex-wrap gap-2">
                    {message.createdResources.map((resource) => (
                      <React.Fragment key={`${resource.type}-${resource.id}`}>
                        <button
                          onClick={() => handleOpenCreatedResource(resource)}
                          className="inline-flex items-center gap-1.5 text-sm font-medium bg-primary-500 text-white px-3 py-1.5 rounded-md hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-1 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                          </svg>
                          Open {resource.name}
                        </button>
                        {/* AC: "Run it now" sibling for a freshly built workflow.
                            Binds the run turn to THIS orchestration via
                            overrideContext (the journey-complete turn is often in
                            'general' context, but the backend run_workflow dispatch
                            binds the orchestration ONLY from
                            context_type==='orchestration' + context_id). This
                            triggers the EXISTING gated run flow — the "Run this
                            workflow?" PreviewCard still gates the actual run.

                            Sent with `silent: true` so the click does NOT fabricate
                            a blue "run the workflow" USER bubble (the user pressed a
                            button, not typed a sentence). An honest transient
                            "Starting the run…" pill shows while the turn
                            round-trips; the backend still receives the run message,
                            so the gated "Run this workflow?" card still surfaces. */}
                        {resource.type === 'workflow' && (
                          <button
                            onClick={() =>
                              handleSendMessage('run the workflow', {
                                silent: true,
                                silentIndicatorText: 'Starting the run…',
                                overrideContext: {
                                  context_type: 'orchestration',
                                  context_id: resource.id,
                                },
                              })
                            }
                            disabled={isLoading}
                            aria-label={`Run ${resource.name}`}
                            className="inline-flex items-center gap-1.5 text-sm font-medium bg-green-600 text-white px-3 py-1.5 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 focus:ring-offset-1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Run it now
                          </button>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              )}

              {/* AC-1645: clarifying questions as one-click answer chips. */}
              {message.role === 'assistant' &&
                message.clarifyingQuestions &&
                message.clarifyingQuestions.length > 0 && (
                  <ClarifyChips
                    questions={message.clarifyingQuestions}
                    onAnswer={handleClarifyAnswer}
                    disabled={isLoading}
                  />
                )}

              {/* AC-1757 (epic AC-1749 Assistant Concierge): the goal-journey
                  progress strip. Renders ABOVE/WITH this turn's pending-action
                  card when the turn carried a journey envelope (read off
                  response.journey on the chat / confirm-builtin / build-confirm /
                  decline responses). Also renders on a card-less complete/blocked
                  turn so the all-set / stop-reason state surfaces. Purely derived
                  from message.journey — no Zustand, no query. */}
              {message.role === 'assistant' && message.journey && (
                <JourneyProgress journey={message.journey} />
              )}

              {/* AC-1646: plan preview card gating creation. The dominant element
                  of the turn — suggested_actions are suppressed until resolved. */}
              {message.role === 'assistant' && message.pendingActionPreview && (
                <>
                  {message.planChanged && (
                    <div
                      role="status"
                      className="mt-2 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded px-2 py-1"
                    >
                      The plan changed and is out of date. Review the updated plan below.
                    </div>
                  )}
                  {/* AC-1790: a configure_schedule preview renders the in-chat
                      ScheduleConfirmCard; AC-1738: a Tier-3 MCP proposal renders
                      the approval-gate McpProposalCard; AC-1746: an
                      unconfigured-built-in renders the in-chat
                      McpConfigureBuiltinCard; every other preview keeps the
                      create/update PreviewCard. A message carries one shape,
                      never more than one. */}
                  {isScheduleConfirmPreview(message.pendingActionPreview) ? (
                    <ScheduleConfirmCard
                      preview={message.pendingActionPreview}
                      state={
                        message.pendingActionPreview.state as
                          | 'pending'
                          | 'confirming'
                          | 'confirmed'
                          | 'declined'
                          | 'superseded'
                      }
                      onConfirm={(req) =>
                        handleConfirmSchedule(
                          message,
                          message.pendingActionPreview as ScheduleConfirmPreview & {
                            state: PreviewState;
                          },
                          req
                        )
                      }
                      onDecline={() =>
                        handleDeclineSchedule(
                          message,
                          message.pendingActionPreview as ScheduleConfirmPreview & {
                            state: PreviewState;
                          }
                        )
                      }
                    />
                  ) : isConfigureBuiltinPreview(message.pendingActionPreview) ? (
                    <McpConfigureBuiltinCard
                      preview={message.pendingActionPreview}
                      state={
                        message.pendingActionPreview.state as
                          | 'pending'
                          | 'configuring'
                          | 'configured'
                          | 'declined'
                          | 'superseded'
                      }
                      onConfirm={(req) =>
                        handleConfirmBuiltin(
                          message,
                          message.pendingActionPreview as McpConfigureBuiltinPreview & {
                            state: PreviewState;
                          },
                          req
                        )
                      }
                      onDecline={() =>
                        handleDeclineBuiltin(
                          message,
                          message.pendingActionPreview as McpConfigureBuiltinPreview & {
                            state: PreviewState;
                          }
                        )
                      }
                    />
                  ) : isMcpProposalPreview(message.pendingActionPreview) ? (
                    <McpProposalCard
                      preview={message.pendingActionPreview}
                      state={
                        message.pendingActionPreview.state as
                          | 'pending'
                          | 'provisioning'
                          | 'provisioned'
                          | 'declined'
                          | 'superseded'
                      }
                      defaultScope="project"
                      onApprove={(req) =>
                        handleApproveMcp(
                          message,
                          message.pendingActionPreview as McpProposalPreview & {
                            state: PreviewState;
                          },
                          req
                        )
                      }
                      onSelectCandidate={() => {
                        /* AC-1739: shortlist re-selection is wired with the
                           server round-trip in the parent epic; the gate stays
                           on the proposed top candidate here. */
                      }}
                      onDecline={() =>
                        handleDeclineMcp(
                          message,
                          message.pendingActionPreview as McpProposalPreview & {
                            state: PreviewState;
                          }
                        )
                      }
                    />
                  ) : (
                    <PreviewCard
                      preview={message.pendingActionPreview as PendingActionPreview}
                      state={
                        message.pendingActionPreview.state as
                          | 'pending'
                          | 'creating'
                          | 'created'
                          | 'declined'
                          | 'superseded'
                      }
                      onConfirm={() =>
                        handleConfirmPreview(
                          message,
                          message.pendingActionPreview as PendingActionPreview & {
                            state: PreviewState;
                          }
                        )
                      }
                      onAdjust={() =>
                        handleAdjustPreview(
                          message.pendingActionPreview as PendingActionPreview & {
                            state: PreviewState;
                          }
                        )
                      }
                      onDecline={() =>
                        handleDeclinePreview(
                          message,
                          message.pendingActionPreview as PendingActionPreview & {
                            state: PreviewState;
                          }
                        )
                      }
                    />
                  )}
                </>
              )}

              {/* AC-1785 (epic AC-1778): live-run card for a workflow the
                  assistant just ran on confirm. Subscribes to
                  useExecution(orchestration_id) for status off the SSE-driven
                  cache + an Open-workflow deep link. */}
              {message.role === 'assistant' && message.runProgress && (
                <CompactRunProgressCard
                  orchestrationId={message.runProgress.orchestrationId}
                  runInstanceId={message.runProgress.runInstanceId}
                  stepCount={message.runProgress.stepCount}
                  projectId={message.runProgress.projectId}
                  workflowName={message.runProgress.workflowName}
                />
              )}

              {/* Show suggested actions — suppressed on clarify/preview turns
                  (the user resolves the question or the plan first). */}
              {message.role === 'assistant' &&
                !(message.clarifyingQuestions && message.clarifyingQuestions.length > 0) &&
                !message.pendingActionPreview &&
                message.suggestedActions && message.suggestedActions.length > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Suggested actions:</div>
                  <div className="flex flex-wrap gap-1">
                    {message.suggestedActions.map((action, index) => (
                      <button
                        key={index}
                        onClick={() => handleSuggestedActionClick(action)}
                        className="text-xs bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-2 py-1 rounded hover:bg-blue-200 dark:hover:bg-blue-900/60 transition-colors"
                      >
                        {action}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}

        {/* AC-1767: while a system-driven auto-continue round-trips, show a
            centered, clearly-automatic indicator INSTEAD of (not stacked with)
            the left-aligned "Thinking..." bubble. Center alignment + pill shape +
            muted neutral color make it structurally distinct from both the
            right-aligned blue user bubble and the left-aligned assistant bubble,
            so the journey advance reads as automatic — never as a user message.
            It is transient: cleared once the next assistant card arrives. */}
        {isAutoContinuing ? (
          <div className="flex justify-center">
            <div
              role="status"
              aria-live="polite"
              className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs text-gray-500 dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-400"
            >
              <svg
                className="h-3.5 w-3.5 animate-spin text-gray-400 motion-reduce:animate-none"
                fill="none"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
              <span>{silentIndicatorText ?? 'Continuing setup…'}</span>
            </div>
          </div>
        ) : isLoading && (
          <div className="flex justify-start">
            <div className="bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-warm-white px-4 py-2 rounded-lg">
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                <span className="text-sm">Thinking...</span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="border-t border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-end space-x-2">
          <textarea
            ref={inputRef}
            rows={3}
            value={inputMessage}
            onChange={handleInputChange}
            onKeyDown={handleKeyPress}
            placeholder="Type your message..."
            aria-label="Type your message (Enter to send, Shift+Enter for a new line)"
            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-warm-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none min-h-[72px] max-h-40 overflow-y-auto"
            disabled={isLoading}
          />
          <button
            onClick={() => handleSendMessage()}
            disabled={!inputMessage.trim() || isLoading}
            aria-label="Send message"
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </div>
      </div>

      {/* Knowledge Base Panel */}
      {isKnowledgePanelOpen && (
        <KnowledgeBasePanel
          isOpen={isKnowledgePanelOpen}
          onClose={() => setIsKnowledgePanelOpen(false)}
          onSelectKnowledge={handleKnowledgeSelect}
          projectId={projectId}
        />
      )}
    </div>
  );
};

export default AssistantChat; 